import React, { FC } from 'react';
import { CrewSchedule, CrewEvent, SingleStaffCrewEvent } from '../../../shared/types/crewSchedule';
import {
  timeToMinutes,
  minutesToTimeString,
  DEFAULT_HOUR_FORMAT,
  DEFAULT_TIMESTR_FORMAT,
} from '@go-shared/lib/time';
import { HourFormat } from '../../../shared/types/brand';
import { capitalize } from '../../../shared/lib/string';
import { Time } from '../../../shared/types/time';
import { InlineIcon } from '@spark-labs/avocado-ui';
import { getDayFirstArrival, getDayLastDeparture, getDayLocation } from '@go-shared/lib/sailing';

export interface CrewDayScheduleProps {
  schedule: CrewSchedule;
}

function getScheduleTime(event: SingleStaffCrewEvent, hourFormat: HourFormat): string {
  let eventStart = timeToMinutes(event.startTime);
  let staffAllocation = event.staffAllocation;

  let startTime = minutesToTimeString(
    eventStart + staffAllocation.startTime,
    hourFormat,
    'compact'
  );
  let endTime = minutesToTimeString(
    eventStart + staffAllocation.startTime + staffAllocation.duration,
    hourFormat,
    'compact'
  );

  return `${startTime} - ${endTime}`;
}

function getEvent(event: SingleStaffCrewEvent): React.ReactNode {
  let staffAllocation = event.staffAllocation;

  let staffActivity = staffAllocation.activity !== '' && (
    <span key="activity">{staffAllocation.activity}</span>
  );
  let eventTitle = (
    <span key="title" className="italic">
      {event.title}
    </span>
  );

  let parts = [];
  if (staffActivity) {
    parts.push(staffActivity);
    parts.push(
      <span key="separator" className="px-1">
        |
      </span>
    );
  }

  parts.push(eventTitle);

  return <>{parts}</>;
}

function getVenue(event: SingleStaffCrewEvent): string {
  let location = event.venue.location ? capitalize(event.venue.location) : '';
  return `@ ${event.venue.name}, Deck ${event.venue.level} ${location}`;
}

// FIXME: This does not support multiple ports!
function getSailingTimes(crewSchedule: CrewSchedule): string {
  let firstArrival = getDayFirstArrival(crewSchedule, DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT);
  let lastDeparture = getDayLastDeparture(
    crewSchedule,
    DEFAULT_HOUR_FORMAT,
    DEFAULT_TIMESTR_FORMAT
  );

  if (firstArrival === '' && lastDeparture === '') return '';

  let sailingTime = '';

  if (firstArrival !== '') {
    sailingTime += `Arrival: ${firstArrival}`;
  }

  if (lastDeparture !== '') {
    if (firstArrival !== '') {
      sailingTime += ' - ';
    }

    sailingTime += `Departure: ${lastDeparture}`;
  }

  return sailingTime;
}

/**
 * Given a list of crew events that contain staff allocations, return a new list with duplicated
 * events where events that have the same staff member appear multiple times also appear more than
 * once, with only one staff allocation per event.
 *
 * For example, if we have a list with the event "Pool Bar" with the captain working from 8am to 9am
 * and then again from 10am to 11am, this function will return a new list with _two_ "Pool Bar"
 * events, where the first one will have a Captain from 8 to 9 and the second one from 10 to 11
 *
 */
function expandCrewEvents(events: CrewEvent[]): SingleStaffCrewEvent[] {
  let expandedEvents: SingleStaffCrewEvent[] = [];

  events.forEach(e => {
    e.staffAllocations.forEach(sa => {
      expandedEvents.push({ ...e, staffAllocation: sa });
    });
  });

  return expandedEvents;
}

type CrewEventSorter = (e1: SingleStaffCrewEvent, e2: SingleStaffCrewEvent) => number;

function makeCrewEventSorter(dayStart: Time): CrewEventSorter {
  let dayStartMinutes = timeToMinutes(dayStart);
  return function sortCrewEvents(e1: SingleStaffCrewEvent, e2: SingleStaffCrewEvent): number {
    let e1StaffAllocation = e1.staffAllocation;
    let e2StaffAllocation = e2.staffAllocation;

    let e1StaffStart = timeToMinutes(e1.startTime, dayStartMinutes) + e1StaffAllocation.startTime;
    let e2StaffStart = timeToMinutes(e2.startTime, dayStartMinutes) + e2StaffAllocation.startTime;

    let e1StaffDuration = e1.duration;
    let e2StaffDuration = e2.duration;

    if (e1StaffStart !== e2StaffStart) return e1StaffStart - e2StaffStart;

    return e1StaffDuration - e2StaffDuration;
  };
}

const CrewDaySchedule: FC<CrewDayScheduleProps> = ({ schedule }) => {
  let events = schedule ? expandCrewEvents(schedule.events) : [];
  let crewEventSorter = makeCrewEventSorter(schedule.dayStart);

  events = events.sort(crewEventSorter);
  return (
    <div>
      <h5 className="mt-3 text-grey-darker">
        <span className="ml-2 font-bold text-xs">Day {schedule.name}</span>
        <span className="ml-2 font-bold text-xs truncate ">{getDayLocation(schedule)}</span>
        <span className="ml-2 text-xs whitespace-no-wrap">{getSailingTimes(schedule)}</span>
      </h5>
      {events.map((event, i) => (
        <div
          key={i}
          className="flex my-2 py-2 border-grey-dark border-l-6 text-grey-darker bg-grey-lighter"
        >
          <div className="px-2">
            <InlineIcon className="w-5 h-5" name={'clock'} />
          </div>
          <div className="mx-1">
            <div className="font-bold">{getScheduleTime(event, schedule.hourFormat)}</div>
            <div className="mt-1">{getEvent(event)}</div>
            <div className="mt-1">{getVenue(event)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CrewDaySchedule;
